import React, { useEffect, useState } from "react";
import axios from "axios";
import Modal from "./Modal";
import "./CustomersTab.css";

export default function CustomersTab({ setIsAnyModalActive }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Profile modal state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileCustomer, setProfileCustomer] = useState(null);
  const [profileOrders, setProfileOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          "https://admin.deedelicious.com/php/get-customers.php"
        );
        setCustomers(res.data.customers || []);
      } catch (err) {
        console.error("Failed to fetch customers:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, []);

  const handleViewProfile = async (customerId) => {
    try {
      setOrdersLoading(true);
      const res = await axios.get(
        `https://admin.deedelicious.com/php/get-customer-orders.php?customer_id=${customerId}`
      );
      setProfileOrders(res.data.orders || []);

      const customer = customers.find((c) => c.id === customerId);
      setProfileCustomer(customer);
      setShowProfileModal(true);
      if (setIsAnyModalActive) setIsAnyModalActive(true);
    } catch (err) {
      console.error("Failed to fetch customer orders:", err);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowProfileModal(false);
    setProfileCustomer(null);
    setProfileOrders([]);
    setExpandedOrderId(null);
    if (setIsAnyModalActive) setIsAnyModalActive(false);
  };

  const toggleOrder = (orderId) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  };

  const calculateSummary = () => {
    if (!profileOrders.length)
      return { totalOrders: 0, lastOrderDate: "-", totalSpent: 0 };
    const totalOrders = profileOrders.length;
    const lastOrderDate = new Date(profileOrders[0].created_at).toLocaleDateString(
      "en-GB"
    );
    const totalSpent = profileOrders.reduce(
      (sum, o) => sum + parseFloat(o.total || 0),
      0
    );
    return { totalOrders, lastOrderDate, totalSpent };
  };

  const { totalOrders, lastOrderDate, totalSpent } = calculateSummary();

  return (
    <div className="customers-tab">
      <h1>Customer Data</h1>

      {loading ? (
        <div className="spinner-overlay">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="customers-table-wrapper">
          <table className="customers-table">
            <thead>
              <tr>
                <th>S/N</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((customer, index) => (
                <tr key={customer.id}>
                  <td>{index + 1}</td>
                  <td>{customer.first_name || "N/A"}</td>
                  <td>{customer.last_name || "N/A"}</td>
                  <td>{customer.email || "N/A"}</td>
                  <td>{customer.full_phone}</td>
                  <td className="action-buttons">
                    <button
                      className="call-btn"
                      onClick={() =>
                        (window.location.href = `tel:${customer.full_phone}`)
                      }
                    >
                      Call
                    </button>
                    <button
                      className="view-profile-btn"
                      onClick={() => handleViewProfile(customer.id)}
                    >
                      View Profile
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showProfileModal && profileCustomer && (
        <Modal
          title={`Profile: ${profileCustomer.first_name} ${profileCustomer.last_name}`}
          onClose={handleCloseModal}
          showCancel={false}
          className="profile-modal"
          showSubmitButton={false}
        >
          {/* Customer info */}
          <div className="profile-summary">
            <p>
              <strong>Total Orders:</strong> {totalOrders}
            </p>
            <p>
              <strong>Most Recent Order:</strong> {lastOrderDate}
            </p>
            <p>
              <strong>Total Spent:</strong> £{totalSpent.toFixed(2)}
            </p>
          </div>

          {ordersLoading ? (
            <p>Loading orders...</p>
          ) : profileOrders.length === 0 ? (
            <p className="no-orders">No orders yet.</p>
          ) : (
            <ul className="customer-orders-list">
              {profileOrders.map((order) => (
                <li
                  key={order.id}
                  className="customer-order-card"
                  onClick={() => toggleOrder(order.id)}
                >
                  <div className="order-date">
                    <span className="order-date-text">
                      {new Date(order.created_at).toLocaleDateString("en-GB")}
                    </span>
                    <span className="order-id">Ref: {order.id}</span>
                  </div>
                  <div className="customer-order-header">
                    <strong>Order #{order.order_number}</strong>{" "}
                    <span>{order.order_type}</span>
                  </div>
                  <p className="customer-order-total">
                    <strong>£{parseFloat(order.total).toFixed(2)}</strong>
                  </p>

                  {expandedOrderId === order.id && (
                    <div className="profile-order-items expanded-background">
                      {/* Required Option */}
                      {order.items.map((item) => {
                        return (
                          <div key={item.id} className="profile-order-item">
                            <div className="profile-order-item-header">
                              <span className="profile-order-item-title">
                                {item.name}{" "}
                                {parseFloat(item.base_price || 0) > 0 &&
                                  ` (£${parseFloat(item.base_price).toFixed(2)})`}
                              </span>
                            </div>

                            {/* Required Option */}
                            {item.required_option_name && (
                              <p className="profile-order-item-option">
                                {item.required_option_name}
                                {parseFloat(item.required_option_price || 0) > 0 &&
                                  ` (£${parseFloat(item.required_option_price).toFixed(
                                    2
                                  )})`}
                              </p>
                            )}

                            {/* Optional Options */}
                            {item.selectedOptionalOptions?.length > 0 && (
                              <div className="profile-order-item-option">
                                <p>
                                  <strong>Optional:</strong>
                                </p>
                                <ul>
                                  {item.selectedOptionalOptions.map((opt, idx) => (
                                    <li key={idx}>
                                      {opt.name} x {opt.quantity}
                                      {parseFloat(opt.price) > 0 &&
                                        ` (£${parseFloat(opt.price).toFixed(2)})`}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Item Total */}
                            <div className="profile-order-item-total-wrapper">
                              <span className="profile-order-item-total-text">
                                x {item.quantity} = £
                                {parseFloat(item.total_price).toFixed(2)}
                              </span>
                            </div>

                            <hr className="profile-order-item-divider" />
                          </div>
                        );
                      })}

                      {/* Service charge */}
                      {order.service_charge != null && order.service_charge > 0 && (
                        <p className="profile-order-notes">
                          <strong>Service Charge:</strong> £
                          {parseFloat(order.service_charge).toFixed(2)}
                        </p>
                      )}

                      {/* Notes */}
                      {order.notes?.trim() && (
                        <p className="profile-order-notes">
                          <strong>Notes:</strong> {order.notes}
                        </p>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Modal>
      )}
    </div>
  );
}
